import React, { useState, useMemo, useEffect } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormModal, Form, SmartFormField, SubmitButton, SmartPillGroup, SmartControl, PillGroup } from "../ui/Form";
import Button from "../ui/Button/Button";
import * as z from "zod";
import useAuthStore from "../../Store/AuthStore";
import { INTERESTS } from "../../Utils/personaUtils";
import { fetchGiftsAPI } from "../../Services/giftService";
import ProductCard from "../Product/ProductCard";
import { updateProfileAPI } from "../../Services/userService";
import useEventStore from "../../Store/FavoriteStore";
import Loader from "../Loader/Loader";
import CustomDatePicker from "../ui/DatePicker";
import { stripEmojis } from "../../Utils/textUtils";

// --- Schemas ---
const profileInfoSchema = z.object({
    name: z.string().min(2, "Name is required"),
    dob: z.date().or(z.string().min(1, "Date of birth is required")),
    gender: z.string().min(1, "Gender is required"),
});

const OnboardingWizard = () => {
    const { t, i18n } = useTranslation();
    const { showOnboarding, setShowOnboarding, userData } = useAuthStore();
    const [step, setStep] = useState(0);
    const [selectedInterests, setSelectedInterests] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [profileData, setProfileData] = useState({
        name: userData?.name || userData?.given_name || "",
        dob: null,
        gender: "Male"
    });

    const genderOptions = useMemo(() => [
        { value: "Male", label: t("gender.male") },
        { value: "Female", label: t("gender.female") },
        { value: "Non Binary", label: t("gender.nonBinary") },
    ], [t]);

    const interestOptions = useMemo(() => INTERESTS.map(interest => {
        const mapping = {
            "🎨 Art and Crafting": "art", "🎮 Gaming": "gaming", "📚 Reading": "reading",
            "🎵 Music": "music", "🎥 Movies and TV Shows": "movies", "🏋️‍♂️ Fitness and Exercise": "fitness",
            "🍳 Cooking and Baking": "cooking", "🌍 Traveling": "traveling", "📸 Photography": "photography",
            "🌱 Gardening": "gardening", "🐾 Pets and Animal Care": "pets", "🚴‍♂️ Outdoor Activities": "outdoor",
            "🧩 Puzzles and Board Games": "puzzles", "🛠️ DIY Projects": "diy", "✍️ Writing or Journaling": "writing",
            "🧘‍♀️ Meditation and Yoga": "meditation", "⚽ Sports": "sports", "💻 Technology and Gadgets": "tech",
            "🏖️ Beach and Water Activities": "beach"
        };
        const key = mapping[interest];
        return { value: interest, label: t(`interestsList.${key}`, { defaultValue: interest }) };
    }), [t]);

    // --- Actions ---
    const clearOnboarding = () => {
        setStep(0);
        setSelectedInterests([]);
        setSelectedProducts([]);
        setProducts([]);
        setProfileData({ name: userData?.name || userData?.given_name || "", dob: null, gender: "Male" });
    };

    const saveOnboardingData = async (additionalData = {}) => {
        try {
            const dataToSave = {
                ...profileData,
                interests: selectedInterests.map(stripEmojis),
                selectedProducts: selectedProducts,
                ...additionalData
            };
            await updateProfileAPI(dataToSave);
        } catch (error) {
            console.error("Onboarding Sync Error:", error);
        }
    };

    const handleProfileInfo = async (data) => {
        const dob = data.dob instanceof Date ? data.dob.toISOString().split('T')[0] : data.dob;
        const newProfileData = { ...data, dob };
        setProfileData(newProfileData);
        await saveOnboardingData({ ...newProfileData });
        setStep(1);
    };

    const handleNextInterests = async () => {
        await saveOnboardingData();
        setStep(2);
        fetchOnboardingProducts();
    };

    const fetchOnboardingProducts = async () => {
        setLoadingProducts(true);
        try {
            const cleanQuery = selectedInterests.length > 0
                ? `i want to get a gift for someone who is interested in ${selectedInterests.map(stripEmojis).join(", ")}`
                : "popular gift ideas";

            const response = await fetchGiftsAPI({
                text: cleanQuery, index_name: "gifts", event_type: "Search", userLocation: 'worldwide', minScore: 1,
                query_param: {
                    user: { active: true, k_value: 50, boost: 10 },
                    tags: { active: true, k_value: 50, boost: 8 },
                    GPT: { active: false, k_value: 50, boost: 10 },
                    assort_based_clicks: false,
                },
            });

            const currentLang = i18n.language.split("-")[0].toLowerCase();
            const mapped = response.map((gift) => ({
                id: gift.mid,
                opId: gift._id,
                title: (currentLang === "de" && gift.translations?.DE?.title) ? gift.translations.DE.title : gift.title,
                link: gift.link,
                price: gift.price,
                image: gift.img,
                country: gift.country,
                lovemeter: gift.loveMeter,
                categories: gift.categories,
                description: gift.description,
            }));
            setProducts(mapped);
        } catch (error) {
            console.error("Failed to fetch onboarding products:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            if (prev.includes(product.id)) return prev.filter(id => id !== product.id);
            if (prev.length < 5) return [...prev, product.id];
            return prev;
        });
    };

    useEffect(() => {
        if (selectedProducts.length === 5 && step === 2) handleNextProducts();
    }, [selectedProducts]);

    const handleNextProducts = async (currentProducts = selectedProducts) => {
        try {
            const { likeGift } = useEventStore.getState();
            for (const giftId of currentProducts) await likeGift(giftId);
            await saveOnboardingData({ selectedProducts: currentProducts });
        } catch (error) {
            console.error("Failed to save products:", error);
        }
        setStep(3);
    };

    const handleSkip = () => {
        if (step === 0) setStep(1);
        else if (step === 1) { setStep(2); fetchOnboardingProducts(); }
        else handleNextProducts();
    };

    const handleFinish = () => {
        setShowOnboarding(false);
        clearOnboarding();
    };

    if (!showOnboarding) return null;

    return (
        <FormModal
            isOpen={showOnboarding}
            onClose={() => { }}
            maxWidth={step === 2 ? "max-w-6xl" : "max-w-md"}
            showCloseButton={false}
        >
            <div className="relative pt-20 px-6">
                {/* --- Header --- */}
                {step < 3 && (
                    <div className="absolute top-6 left-0 right-0 flex items-center justify-between px-6">
                        <div className="w-10" />
                        <div className="flex gap-2 items-center">
                            {[0, 1, 2, 3].map((s) => (
                                <div
                                    key={s}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${step === s ? 'bg-red-500 w-5' : 'bg-neutral-200 cursor-pointer hover:bg-neutral-300'}`}
                                    onClick={() => s < step && setStep(s)}
                                />
                            ))}
                        </div>
                        <div className="min-w-10 flex justify-end">
                            <Button variant="ghost" size="sm" onClick={handleSkip} className="text-neutral-green-600 hover:text-red-500 font-medium p-0">
                                {t("onboarding.skip")}
                            </Button>
                        </div>
                    </div>
                )}

                {/* --- Step Content --- */}
                {step === 0 && (
                    <div className="space-y-6">
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold text-black px-8">
                                {t("onboarding.profileTitle")}
                            </h2>
                            <p className="text-sm text-neutral-green-600 px-4">
                                {t("onboarding.profileSubtitle")}
                            </p>
                        </div>

                        <Form
                            schema={profileInfoSchema}
                            onSubmit={handleProfileInfo}
                            defaultValues={profileData}
                            className="space-y-4"
                        >
                            <SmartFormField
                                name="name"
                                label={t("onboarding.nameLabel")}
                                required
                                inputProps={{ placeholder: t("onboarding.nameLabel") }}
                            />

                            <div className="space-y-2 text-left">
                                <label className="text-sm font-medium text-neutral-green-700">
                                    {t("onboarding.dobLabel")}
                                </label>
                                <SmartControl
                                    name="dob"
                                    render={({ value, onChange }) => (
                                        <CustomDatePicker
                                            selected={value ? (value instanceof Date ? value : new Date(value)) : null}
                                            onChange={onChange}
                                            maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 10))}
                                            minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 100))}
                                            placeholderText={t("onboarding.dobLabel")}
                                            className="block w-full px-4 py-2 border rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                            dateFormat="yyyy-MM-dd"
                                            showTimeSelect={false}
                                        />
                                    )}
                                />
                            </div>

                            <div className="space-y-2 text-left">
                                <label className="text-sm font-medium text-neutral-green-700">
                                    {t("onboarding.genderLabel")}
                                </label>
                                <SmartPillGroup
                                    name="gender"
                                    options={genderOptions}
                                    multiple={false}
                                    className="justify-start"
                                />
                            </div>

                            <SubmitButton variant="primary" size="lg" rounded="full" fullWidth className="mt-6 shadow-lg shadow-red-100" tabIndex={0}>
                                {t("onboarding.next")}
                            </SubmitButton>
                        </Form>
                    </div>
                )}

                {step === 1 && (
                    <div className="space-y-6">
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold text-black px-8">
                                {t("onboarding.interestsTitle")}
                            </h2>
                            <p className="text-sm text-neutral-green-600 px-4">
                                {t("onboarding.interestsSubtitle")}
                            </p>
                        </div>

                        <div className="max-h-[40vh] overflow-y-auto px-2 py-4">
                            <PillGroup
                                options={interestOptions}
                                selected={selectedInterests}
                                onSelect={setSelectedInterests}
                                multiple
                                size="xs"
                                className="justify-center"
                            />
                        </div>

                        <Button onClick={handleNextInterests} variant="primary" size="lg" rounded="full" fullWidth className="mt-6 shadow-lg shadow-red-100" tabIndex={0}>
                            {t("onboarding.next")}
                        </Button>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6">
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold text-black">
                                {t("onboarding.giftsTitle")}
                            </h2>
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div
                                        key={i}
                                        className={`w-3 h-3 rounded-full ${i <= selectedProducts.length ? 'bg-red-500' : 'bg-neutral-200'}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="max-h-[65vh] overflow-y-auto px-2 py-4 flex flex-wrap items-stretch justify-center gap-4 sm:gap-6">
                            {loadingProducts ? (
                                <div className="col-span-full flex justify-center py-20 w-full">
                                    <Loader />
                                </div>
                            ) : (
                                products.map((product) => {
                                    const isSelected = selectedProducts.includes(product.id);
                                    return (
                                        <div key={product.id} className="w-[calc(50%-8px)] sm:w-auto flex justify-center">
                                            <ProductCard
                                                {...product}
                                                variant="search"
                                                showCategories={false}
                                                showLovemeter={false}
                                                showExplanation={false}
                                                showFavoriteButton={false}
                                                isSelected={isSelected}
                                                containerClassName="products-mini-contain !m-0 !mb-0"
                                                addToCartText={isSelected ? t("onboarding.selected") : t("onboarding.select")}
                                                onAddToCart={() => toggleProductSelection(product)}
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="py-2 space-y-10 text-center text-left-rtl">
                        <div className="space-y-4">
                            <div className="flex justify-center">
                                <div className="relative">
                                    <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center shadow-inner">
                                        <Check size={48} className="text-red-500" strokeWidth={3} />
                                    </div>
                                </div>
                            </div>
                            <h2 className="text-3xl font-bold text-black px-4">
                                {t("onboarding.thankYouTitle")}
                            </h2>
                            <p className="text-lg text-neutral-green-600 px-8 leading-relaxed">
                                {t("onboarding.thankYouSubtitle")}
                            </p>
                        </div>

                        <Button onClick={handleFinish} variant="primary" size="lg" rounded="full" fullWidth className="shadow-lg shadow-red-100 font-bold h-14 text-lg" tabIndex={0}>
                            {t("onboarding.finish")}
                        </Button>
                    </div>
                )}
            </div>
        </FormModal>
    );
};

export default OnboardingWizard;
